import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
  fullWidth?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-gradient-to-b from-primary-500 to-primary-600 text-white shadow-sm ring-1 ring-primary-700/30 hover:from-primary-500 hover:to-primary-700 active:to-primary-800 disabled:from-primary-400 disabled:to-primary-400 disabled:ring-0',
  secondary:
    'bg-surface-subtle text-fg hover:bg-surface-subtle/80 disabled:opacity-50',
  ghost:
    'bg-transparent text-fg-muted hover:bg-surface-subtle hover:text-fg disabled:opacity-50',
  danger:
    'bg-gradient-to-b from-rose-500 to-rose-600 text-white shadow-sm ring-1 ring-rose-700/30 hover:from-rose-500 hover:to-rose-700 active:to-rose-800 disabled:from-rose-400 disabled:to-rose-400 disabled:ring-0',
  outline:
    'border border-border bg-surface text-fg hover:bg-surface-muted disabled:opacity-50 shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs rounded-lg',
  md: 'h-9 px-4 text-sm rounded-lg',
  lg: 'h-11 px-6 text-[15px] rounded-xl',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading,
    fullWidth,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium',
        'transition-all duration-150 active:scale-[0.97]',
        'disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});
